import { useState } from 'react';
import { Box } from '@mui/material';
import { Navigation, type AppView } from '../Header/Navigation';
import { DroneMap } from '../Map/DroneMap';
import { DroneDetailsPanel } from '../DronePanel/DroneDetailsPanel';
import { HomeBasePanel } from '../HomeBase/HomeBasePanel';
import { ArchitectureAgent } from '../Tooling/ArchitectureAgent';
import { MissionPlanner } from '../Planner/MissionPlanner';
import { UserAdminPanel } from '../Admin/UserAdminPanel';
import { SettingsPanel } from '../Settings/SettingsPanel';
import { FleetGallery } from '../Fleet/FleetGallery';
import { useAdminAccess } from '../../hooks/useAdminAccess';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

export const MainLayout: React.FC = () => {
  const selectedDroneId = useSelector((state: RootState) => state.drones.selectedDroneId);
  const [currentView, setCurrentView] = useState<AppView>('map');
  const isAdmin = useAdminAccess();

  // The layout fills #root rather than restating a viewport unit: #root owns
  // the dynamic-viewport height, so there is one place to get mobile right and
  // no second box that outgrows the visible area on iOS Safari (#399).
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Navigation
        currentView={currentView}
        onViewChange={setCurrentView}
        showAdmin={isAdmin}
      />
      {currentView === 'map' && (
        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
          <Box sx={{ flexGrow: 1, position: 'relative' }}>
            {/* Enrolment lives inside the map's control column since #521 —
                it used to sit at top-left, on top of Leaflet's zoom buttons. */}
            {/* Registering a drone of either kind is one control inside the
                map's own column (#389) — there is no longer a second floating
                action in the opposite corner. */}
            <DroneMap />
          </Box>
          {selectedDroneId && (
            <Box
              sx={{
                width: { xs: '100%', md: 400 },
                height: '100%',
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <DroneDetailsPanel />
              <HomeBasePanel />
            </Box>
          )}
        </Box>
      )}
      {/* Every drone, parked ones included, as rows or tiles (#412). */}
      {currentView === 'fleet' && (
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <FleetGallery />
        </Box>
      )}
      {currentView === 'planner' && <MissionPlanner />}
      {currentView === 'tooling' && (
        <Box sx={{ flexGrow: 1, overflow: 'auto', bgcolor: '#0d0f1a' }}>
          <ArchitectureAgent />
        </Box>
      )}
      {/* Admin-only user management (#379). The tab is hidden for non-admins,
          and the API rejects them regardless. */}
      {currentView === 'admin' && isAdmin && (
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <UserAdminPanel />
        </Box>
      )}
      {/* Self-service profile and preferences (#378). Available to every signed-in
          user — it only ever shows their own account. */}
      {currentView === 'settings' && (
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <SettingsPanel />
        </Box>
      )}
    </Box>
  );
};
